import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import VideoWithSpeed from '../../components/VideoWithSpeed';
import { publicApi, DenemeBookDetail, DenemeVideo } from '../../services/api';

const F = { fontFamily: 'Inter' } as const;

// Public solution-video viewer for a single "Deneme Sınavı" book. Subject tabs
// across the top, a grid of solution videos for the active subject, and a
// full-screen player (VideoWithSpeed) with prev/next. Videos only — no PDF,
// no login, no progress tracking.
export default function DenemeSinaviBook() {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [book, setBook] = useState<DenemeBookDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [playerIdx, setPlayerIdx] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    publicApi.denemeBookContent(bookId!)
      .then((r) => { if (alive) setBook(r.data); })
      .catch((e) => { if (alive) { if (e?.response?.status === 404) setNotFound(true); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [bookId]);

  const sections = book?.sections ?? [];
  const activeSection = sections[activeIdx];

  // Flatten the active subject's playable videos (lesson video first, if any).
  const videos: DenemeVideo[] = useMemo(() => {
    if (!activeSection) return [];
    const list: DenemeVideo[] = [];
    if (activeSection.lesson_video_url) {
      list.push({ id: `lesson-${activeIdx}`, title: 'Ders Videosu', short_title: null, url: activeSection.lesson_video_url });
    }
    return list.concat(activeSection.videos.filter((v) => !!v.url));
  }, [activeSection, activeIdx]);

  const closePlayer = useCallback(() => setPlayerIdx(null), []);
  const go = useCallback((delta: number) => {
    setPlayerIdx((cur) => {
      if (cur == null) return cur;
      const next = cur + delta;
      return next >= 0 && next < videos.length ? next : cur;
    });
  }, [videos.length]);

  useEffect(() => {
    if (playerIdx == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePlayer();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playerIdx, go, closePlayer]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f7fb] flex items-center justify-center" style={F}>
        <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !book) {
    return (
      <div className="min-h-screen bg-[#f6f7fb] flex flex-col items-center justify-center gap-4 px-6 text-center" style={F}>
        <p className="text-[18px] font-semibold text-[#2b2b2b]">Deneme sınavı bulunamadı.</p>
        <button onClick={() => navigate('/deneme-sinavi-videolari')} className="text-[#3973bf] font-semibold hover:underline">
          ← Tüm deneme sınavları
        </button>
      </div>
    );
  }

  const activeVideo = playerIdx != null ? videos[playerIdx] : null;

  return (
    <div className="min-h-screen bg-[#f6f7fb]" style={F}>
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => navigate('/deneme-sinavi-videolari')}
              className="text-[14px] font-semibold text-[#3973bf] hover:underline whitespace-nowrap"
            >
              ← Geri
            </button>
            <h1 className="text-[15px] lg:text-[18px] font-semibold text-[#2b2b2b] truncate">{book.display_name || book.title}</h1>
          </div>
          <BrandLogo variant="mark" className="h-[30px] lg:h-[34px] object-contain shrink-0" />
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 lg:px-8 py-7">
        {sections.length === 0 ? (
          <p className="text-center text-gray-400 py-20">Bu deneme için henüz çözüm videosu eklenmemiş.</p>
        ) : (
          <>
            {/* Subject tabs */}
            <div className="flex gap-2 overflow-x-auto pb-3 mb-5 -mx-1 px-1">
              {sections.map((s, i) => (
                <button
                  key={s.section_label + i}
                  onClick={() => { setActiveIdx(i); setPlayerIdx(null); }}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-[13px] lg:text-[14px] font-medium border transition-colors ${
                    i === activeIdx
                      ? 'bg-[#3973bf] text-white border-[#3973bf]'
                      : 'bg-white text-[#2b2b2b] border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {s.section_topic || s.section_display_label || s.section_label}
                  <span className={`ml-2 text-[11px] ${i === activeIdx ? 'text-white/80' : 'text-[#999]'}`}>
                    {(s.videos.length + (s.lesson_video_url ? 1 : 0))}
                  </span>
                </button>
              ))}
            </div>

            {/* Video grid for the active subject */}
            {videos.length === 0 ? (
              <p className="text-center text-gray-400 py-16">Bu bölümde video yok.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {videos.map((v, i) => (
                  <button
                    key={v.id}
                    onClick={() => setPlayerIdx(i)}
                    className="group bg-white rounded-[14px] border border-gray-100 p-3 flex flex-col items-center gap-2 hover:border-[#e5599b] hover:shadow-md transition-all"
                    title={v.title}
                  >
                    <span className="w-10 h-10 rounded-full bg-[#fdecf4] text-[#e5599b] flex items-center justify-center text-[15px] group-hover:bg-[#e5599b] group-hover:text-white transition-colors">▶</span>
                    <span className="text-[12px] font-medium text-[#2b2b2b] text-center leading-tight line-clamp-2">{v.short_title || v.title}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Full-screen player */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col" style={F}>
          <div className="flex items-center justify-between px-5 lg:px-8 h-[60px] border-b border-gray-100 shrink-0">
            <div className="min-w-0">
              <p className="text-[12px] text-[#888] truncate">{activeSection?.section_topic || activeSection?.section_display_label || activeSection?.section_label}</p>
              <p className="text-[15px] font-semibold text-[#2b2b2b] truncate">{activeVideo.title}</p>
            </div>
            <button onClick={closePlayer} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-gray-100 shrink-0" aria-label="Kapat">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
                <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>
          </div>

          <div className="flex-1 min-h-0 bg-black flex items-center justify-center">
            {activeVideo.url
              ? <VideoWithSpeed src={activeVideo.url} autoPlay wrapperClassName="w-full h-full flex items-center justify-center" className="max-h-[80vh] max-w-full" />
              : <p className="text-white/70">Bu soru için çözüm videosu henüz yok.</p>}
          </div>

          {/* Nav */}
          <div className="flex items-center justify-between px-5 lg:px-8 h-[64px] border-t border-gray-100 shrink-0 gap-4">
            <button
              onClick={() => go(-1)}
              disabled={playerIdx === 0}
              className="rounded-[12px] px-5 py-2.5 text-[14px] font-semibold text-[#2b2b2b] bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              ← Önceki
            </button>
            <div className="flex-1 overflow-x-auto flex items-center justify-center gap-1.5">
              {videos.map((v, i) => (
                <button
                  key={v.id}
                  onClick={() => setPlayerIdx(i)}
                  className={`w-8 h-8 rounded-lg text-[12px] font-semibold shrink-0 ${
                    i === playerIdx ? 'bg-[#e5599b] text-white' : 'bg-gray-100 text-[#2b2b2b] hover:bg-gray-200'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() => go(1)}
              disabled={playerIdx === videos.length - 1}
              className="rounded-[12px] px-5 py-2.5 text-[14px] font-semibold text-white bg-[#3973bf] hover:bg-blue-700 disabled:opacity-40"
            >
              Sonraki →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
